import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit3, Trash2, Eye, BookOpen, Calendar,
  ChevronLeft, ChevronRight, X, GraduationCap, Users,
  Building2, Target, FileText, UserCircle, ClipboardList, Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

const initialForm = {
  descr_curso: '',
  nome_facilitador: '',
  dt_inicio: '',
  dt_fim: '',
  realiza: '',
  organiza: '',
  patrocinio: '',
  objetivo: '',
  programa: '',
  publico: '',
  curriculo: ''
};

/**
 * Calcula a situação do curso com base nas datas.
 */
const getSituacao = (dtInicio, dtFim) => {
  if (!dtInicio || !dtFim) return { label: '-', className: 'bg-gray-100 text-gray-600' };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const inicio = new Date(dtInicio);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(dtFim);
  fim.setHours(0, 0, 0, 0);

  if (fim < hoje) return { label: 'Encerrado', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
  if (inicio > hoje) return { label: 'Agendado', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  return { label: 'Em andamento', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
};

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
      <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
    </div>
    <div className="p-4 space-y-4">
      {children}
    </div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm";
const textareaClass = `${inputClass} min-h-[100px] resize-y`;

const Cursos = () => {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingCurso, setViewingCurso] = useState(null);
  const [editingCurso, setEditingCurso] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => { loadCursos(); }, [page, filtroSituacao, filtroDataInicio, filtroDataFim]);
  useEffect(() => { document.title = 'Cursos - EstOX'; }, []);

  const loadCursos = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filtroSituacao) params.situacao = filtroSituacao;
      if (filtroDataInicio) params.data_inicio = filtroDataInicio;
      if (filtroDataFim) params.data_fim = filtroDataFim;

      const response = await api.get('/cursos', { params });
      setCursos(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      toast.error('Erro ao carregar cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadCursos();
  };

  const openModal = (curso = null) => {
    setEditingCurso(curso);
    setViewingCurso(null);
    setFormData({
      descr_curso: curso?.descr_curso || '',
      nome_facilitador: curso?.nome_facilitador || '',
      dt_inicio: curso?.dt_inicio ? curso.dt_inicio.slice(0, 10) : '',
      dt_fim: curso?.dt_fim ? curso.dt_fim.slice(0, 10) : '',
      realiza: curso?.realiza || '',
      organiza: curso?.organiza || '',
      patrocinio: curso?.patrocinio || '',
      objetivo: curso?.objetivo || '',
      programa: curso?.programa || '',
      publico: curso?.publico || '',
      curriculo: curso?.curriculo || ''
    });
    setShowModal(true);
  };

  const openView = (curso) => {
    setViewingCurso(curso);
    setEditingCurso(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCurso(null);
    setViewingCurso(null);
    setFormData(initialForm);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.descr_curso.trim()) return toast.error('Descrição do curso é obrigatória');
    if (!formData.nome_facilitador.trim()) return toast.error('Nome do facilitador é obrigatório');
    if (!formData.dt_inicio) return toast.error('Data de início é obrigatória');
    if (!formData.dt_fim) return toast.error('Data de término é obrigatória');
    if (new Date(formData.dt_fim) < new Date(formData.dt_inicio)) {
      return toast.error('A data de término não pode ser anterior à data de início');
    }

    setIsSaving(true);
    try {
      if (editingCurso) {
        await api.put(`/cursos/${editingCurso.id_curso}`, formData);
        toast.success('Curso atualizado com sucesso!');
      } else {
        await api.post('/cursos', formData);
        toast.success('Curso criado com sucesso!');
      }
      closeModal();
      loadCursos();
    } catch (error) {
      console.error('Erro ao salvar curso:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar curso';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cursos/${deleteTarget.id_curso}`);
      toast.success('Curso excluído com sucesso!');
      setDeleteTarget(null);
      loadCursos();
    } catch (error) {
      console.error('Erro ao excluir curso:', error);
      toast.error('Erro ao excluir curso');
    }
  };

  const isViewing = !!viewingCurso;
  const displayCurso = viewingCurso || editingCurso;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Cursos</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerencie os cursos de capacitação e educação</p>
        </div>
        <IconButton
          icon={Plus}
          onClick={() => openModal()}
          variant="primary"
          title="Novo Curso"
        />
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total de cursos</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{total}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Em andamento</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {cursos.filter(c => getSituacao(c.dt_inicio, c.dt_fim).label === 'Em andamento').length}
            </p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Agendados</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {cursos.filter(c => getSituacao(c.dt_inicio, c.dt_fim).label === 'Agendado').length}
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por descrição, facilitador ou instituição..."
              className={`${inputClass} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <select
            className={`${inputClass} lg:w-44`}
            value={filtroSituacao}
            onChange={(e) => { setFiltroSituacao(e.target.value); setPage(1); }}
          >
            <option value="">Todas as situações</option>
            <option value="agendado">Agendado</option>
            <option value="andamento">Em andamento</option>
            <option value="encerrado">Encerrado</option>
          </select>
          <input
            type="date"
            className={`${inputClass} lg:w-40`}
            title="Data de início (de)"
            value={filtroDataInicio}
            onChange={(e) => { setFiltroDataInicio(e.target.value); setPage(1); }}
          />
          <input
            type="date"
            className={`${inputClass} lg:w-40`}
            title="Data de início (até)"
            value={filtroDataFim}
            onChange={(e) => { setFiltroDataFim(e.target.value); setPage(1); }}
          />
          <IconButton icon={Search} onClick={handleSearch} variant="primary" title="Buscar" />
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Facilitador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Início</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Término</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Situação</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : cursos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <BookOpen className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nenhum curso encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {searchTerm || filtroSituacao ? 'Tente ajustar os filtros.' : 'Comece criando um novo curso.'}
                    </p>
                  </td>
                </tr>
              ) : (
                cursos.map((c) => {
                  const sit = getSituacao(c.dt_inicio, c.dt_fim);
                  return (
                    <tr key={c.id_curso} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">#{c.id_curso}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{c.descr_curso}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{c.nome_facilitador || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{formatDate(c.dt_inicio)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{formatDate(c.dt_fim)}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sit.className}`}>
                          {sit.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <IconButton icon={Eye} onClick={() => openView(c)} variant="info" size="sm" title="Visualizar" />
                          <IconButton icon={Edit3} onClick={() => openModal(c)} variant="warning" size="sm" title="Editar" />
                          <IconButton icon={Trash2} onClick={() => setDeleteTarget(c)} variant="danger" size="sm" title="Excluir" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Página {page} de {totalPages} · {total} registro(s)
            </p>
            <div className="flex items-center gap-2">
              <IconButton icon={ChevronLeft} onClick={() => setPage(p => Math.max(1, p - 1))} variant="secondary" size="sm" title="Anterior" disabled={page <= 1} />
              <IconButton icon={ChevronRight} onClick={() => setPage(p => Math.min(totalPages, p + 1))} variant="secondary" size="sm" title="Próximo" disabled={page >= totalPages} />
            </div>
          </div>
        )}
      </div>

      {/* Modal de formulário / visualização */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative top-10 w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            {/* Cabeçalho do modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isViewing ? 'Detalhes do Curso' : editingCurso ? 'Editar Curso' : 'Novo Curso'}
                </h3>
              </div>
              <IconButton icon={X} onClick={closeModal} variant="secondary" size="sm" title="Fechar" />
            </div>

            {/* Corpo do modal */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {isViewing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-gray-500 uppercase">Descrição</p><p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{displayCurso?.descr_curso || '-'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Facilitador</p><p className="text-sm text-gray-900 dark:text-white mt-1">{displayCurso?.nome_facilitador || '-'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Início</p><p className="text-sm text-gray-900 dark:text-white mt-1">{formatDate(displayCurso?.dt_inicio)}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Término</p><p className="text-sm text-gray-900 dark:text-white mt-1">{formatDate(displayCurso?.dt_fim)}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Realiza</p><p className="text-sm text-gray-900 dark:text-white mt-1">{displayCurso?.realiza || '-'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Organiza</p><p className="text-sm text-gray-900 dark:text-white mt-1">{displayCurso?.organiza || '-'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Patrocínio</p><p className="text-sm text-gray-900 dark:text-white mt-1">{displayCurso?.patrocinio || '-'}</p></div>
                    <div><p className="text-xs text-gray-500 uppercase">Público-alvo</p><p className="text-sm text-gray-900 dark:text-white mt-1">{displayCurso?.publico || '-'}</p></div>
                  </div>
                  <div><p className="text-xs text-gray-500 uppercase">Objetivo</p><p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{displayCurso?.objetivo || '-'}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase">Programa</p><p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{displayCurso?.programa || '-'}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase">Currículo do facilitador</p><p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{displayCurso?.curriculo || '-'}</p></div>
                </div>
              ) : (
                <form onSubmit={handleSave} className="space-y-5">
                  <SectionCard icon={BookOpen} title="Informações gerais">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Descrição do curso" required>
                        <input type="text" maxLength={80} className={inputClass} value={formData.descr_curso} onChange={(e) => setFormData({ ...formData, descr_curso: e.target.value })} placeholder="Ex: Curso de capacitação em Excel" />
                      </Field>
                      <Field label="Facilitador" required>
                        <input type="text" maxLength={120} className={inputClass} value={formData.nome_facilitador} onChange={(e) => setFormData({ ...formData, nome_facilitador: e.target.value })} placeholder="Nome do facilitador" />
                      </Field>
                      <Field label="Data de início" required>
                        <input type="date" className={inputClass} value={formData.dt_inicio} onChange={(e) => setFormData({ ...formData, dt_inicio: e.target.value })} />
                      </Field>
                      <Field label="Data de término" required>
                        <input type="date" className={inputClass} value={formData.dt_fim} onChange={(e) => setFormData({ ...formData, dt_fim: e.target.value })} />
                      </Field>
                    </div>
                  </SectionCard>

                  <SectionCard icon={Building2} title="Realização e organização">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Realiza">
                        <input type="text" maxLength={80} className={inputClass} value={formData.realiza} onChange={(e) => setFormData({ ...formData, realiza: e.target.value })} />
                      </Field>
                      <Field label="Organiza">
                        <input type="text" maxLength={80} className={inputClass} value={formData.organiza} onChange={(e) => setFormData({ ...formData, organiza: e.target.value })} />
                      </Field>
                      <Field label="Patrocínio">
                        <input type="text" maxLength={80} className={inputClass} value={formData.patrocinio} onChange={(e) => setFormData({ ...formData, patrocinio: e.target.value })} />
                      </Field>
                    </div>
                  </SectionCard>

                  <SectionCard icon={Target} title="Detalhes do curso">
                    <div className="space-y-4">
                      <Field label="Objetivo">
                        <input type="text" maxLength={250} className={inputClass} value={formData.objetivo} onChange={(e) => setFormData({ ...formData, objetivo: e.target.value })} />
                      </Field>
                      <Field label="Público-alvo">
                        <input type="text" maxLength={250} className={inputClass} value={formData.publico} onChange={(e) => setFormData({ ...formData, publico: e.target.value })} />
                      </Field>
                      <Field label="Programa e conteúdo">
                        <textarea className={textareaClass} value={formData.programa} onChange={(e) => setFormData({ ...formData, programa: e.target.value })} placeholder="Descreva o programa e conteúdo do curso..." />
                      </Field>
                      <Field label="Currículo do facilitador">
                        <textarea className={textareaClass} value={formData.curriculo} onChange={(e) => setFormData({ ...formData, curriculo: e.target.value })} placeholder="Descreva o currículo do facilitador..." />
                      </Field>
                    </div>
                  </SectionCard>

                  <div className="flex justify-end gap-3 pt-2">
                    <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                    <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : editingCurso ? 'Atualizar' : 'Salvar'} />
                  </div>
                </form>
              )}
            </div>

            {/* Rodapé do modal (visualização) */}
            {isViewing && (
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <IconButton icon={X} onClick={closeModal} variant="secondary" title="Fechar" />
                <IconButton icon={Edit3} onClick={() => openModal(displayCurso)} variant="warning" title="Editar" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Excluir curso</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir o curso <strong>"{deleteTarget.descr_curso}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <IconButton icon={X} onClick={() => setDeleteTarget(null)} variant="secondary" title="Cancelar" />
              <IconButton icon={Trash2} onClick={confirmDelete} variant="danger" title="Excluir" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cursos;
